// src/pages/DashboardNavigation/DashboardNavigation.jsx

import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";

import { getSettings } from "./../../services/settingsServices";

const DISPLAY_BY_PATH_RAW = {
  // Utilisateur
  "/absences": "display_absences",
  "/calendrier": "display_calendrier",
  "/chat": "display_chat",
  "/boutique": "display_boutique",
  "/commandes-&-reglements": "display_commandes",
  "/prepa-physique": "display_prepa_physique",
  "/actualites": "display_news",
  "/galerie-photos": "display_galerie",
  "/lexique": "display_lexique",
  "/arbitrage-judo": "display_arbitrage",
  "/applications-judo": "display_applications",
  "/documents-passage-ceinture": "display_passage_ceinture_documents",
  "/techniques-judo": "display_techniques",
  "/techniques-quizz": "display_techniquesquizz",
  "/vote-sondage-utilisateur": "display_vote",

  // Enseignant
  "/appel-presence": "display_appels",
  "/tableau-appel": "display_tableauappel",
  "/passage-ceinture": "display_passage",
  "/grade-cn": "display_gradecn",
  "/evenements": "display_evenements",
  "/prepa-physique-coaching": "display_prepa_physique_coaching",
  "/suivi-competitions": "display_suivicompetition",
  "/palmares": "display_palmares",
  "/objectifs": "display_objectifs",
  "/todolist": "display_todolist",
  "/techniques-enseignants": "display_techniquesenseignants",
  "/categories": "display_categories",

  // Admin
  "/articles-admin": "display_articlesadmin",
  "/depenses-recettes": "display_depensesrecettes",
  "/coachings": "display_coaching",
  "/heures-enseignement": "display_heures_enseignants",
  "/inventaire": "display_inventaire",
  "/pret-materiel": "display_pretmateriel",
  "/chat-page-admin": "display_chat_admin",
  "/message-admin": "display_messages_admin",
  "/vote-sondage-admin": "display_voteadmin",
  "/pages-personnalisees": "display_pagespersonnalisees",
};

const PATH_LABELS = {
  "/tableau-de-bord": "Tableau de bord",
  "/dossier-d-inscription": "Dossier d'inscription",
  "/fiche-adherent": "Ma fiche adhérent",
  "/absences": "Absences",
  "/calendrier": "Calendrier",
  "/messages": "Messages",
  "/chat": "Chat",
  "/boutique": "Boutique",
  "/commandes-&-reglements": "Commandes & règlements",
  "/actualites": "Actualités",
  "/galerie-photos": "Galerie photos",
  "/lexique": "Lexique",
  "/arbitrage-judo": "Arbitrage Judo",
  "/applications-judo": "Applications Judo",
  "/prepa-physique": "Prépa physique",
  "/documents-passage-ceinture": "Documents ceinture",
  "/techniques-judo": "Techniques Judo",
  "/techniques-quizz": "Quiz techniques",
  "/vote-sondage-utilisateur": "Votes & sondages",

  "/listing-adherents": "Listing adhérents",
  "/appel-presence": "Appel présence",
  "/tableau-appel": "Tableau d'appel",
  "/passage-ceinture": "Passage ceinture",
  "/grade-cn": "Grades CN",
  "/prepa-physique-coaching": "Coaching physique",
  "/suivi-competitions": "Suivi compétitions",
  "/evenements": "Évènements",
  "/palmares": "Palmarès",
  "/objectifs": "Objectifs",
  "/todolist": "Todo list",
  "/techniques-enseignants": "Techniques enseignants",
  "/categories": "Catégories",
  "/aide-enseignants": "Aide enseignants",

  "/dashboard-adherents": "Dashboard adhérents",
  "/dashboard-comptabilite": "Comptabilité",
  "/details-adherent": "Détails adhérent",
  "/listing-utilisateurs": "Utilisateurs",
  "/listing-adherents-admin": "Adhérents admin",
  "/reglements-inscriptions": "Règlements inscriptions",
  "/dossiers-d-inscription": "Dossiers inscription",
  "/paiements": "Paiements",
  "/articles-admin": "Articles",
  "/depenses-recettes": "Dépenses / recettes",
  "/coachings": "Coachings",
  "/heures-enseignement": "Heures enseignement",
  "/inventaire": "Inventaire",
  "/pret-materiel": "Prêt matériel",
  "/chat-page-admin": "Chat admin",
  "/message-admin": "Messages admin",
  "/vote-sondage-admin": "Votes admin",
  "/pages-personnalisees": "Pages personnalisées",
  "/parametres": "Paramètres",
  "/requetes-admin": "Requêtes",
  "/abonnement": "Abonnement",
  "/update": "Mises à jour",
  "/aide-conseils": "Aide & conseils",
};

const normalizePath = (path) =>
  (path || "")
    .toLowerCase()
    .replace(/\/$/, "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const DISPLAY_BY_PATH = Object.fromEntries(
  Object.entries(DISPLAY_BY_PATH_RAW).map(([path, key]) => [
    normalizePath(path),
    key,
  ])
);

const unwrapSettings = (settings) => {
  if (!settings) return {};

  let output = settings;

  if (Array.isArray(output)) output = output[0] || {};
  if (output?.dataValues) output = output.dataValues;

  return output || {};
};

const isTrue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return ["true", "1", "yes", "on"].includes(normalized);
  }

  return Boolean(value);
};

const toRoleKey = (role) => {
  if (role === 2 || /^utilisateur|user$/i.test(String(role))) return "user";

  if (role === 3 || /^enseignant|teacher$/i.test(String(role))) {
    return "enseignant";
  }

  return "admin";
};

const paths = {
  user: [
    "/tableau-de-bord",
    "/dossier-d-inscription",
    "/fiche-adherent",
    "/absences",
    "/calendrier",
    "/messages",
    "/chat",
    "/boutique",
    "/commandes-&-reglements",
    "/actualites",
    "/galerie-photos",
    "/lexique",
    "/arbitrage-judo",
    "/applications-judo",
    "/prepa-physique",
    "/documents-passage-ceinture",
    "/techniques-judo",
    "/techniques-quizz",
    "/vote-sondage-utilisateur",
  ],

  enseignant: [
    "/tableau-de-bord",
    "/dossier-d-inscription",
    "/fiche-adherent",
    "/absences",
    "/calendrier",
    "/messages",
    "/chat",
    "/boutique",
    "/commandes-&-reglements",
    "/actualites",
    "/galerie-photos",
    "/lexique",
    "/arbitrage-judo",
    "/applications-judo",
    "/prepa-physique",
    "/documents-passage-ceinture",
    "/techniques-judo",
    "/techniques-quizz",
    "/vote-sondage-utilisateur",
    "/listing-adherents",
    "/appel-presence",
    "/tableau-appel",
    "/passage-ceinture",
    "/grade-cn",
    "/prepa-physique-coaching",
    "/suivi-competitions",
    "/evenements",
    "/palmares",
    "/objectifs",
    "/todolist",
    "/techniques-enseignants",
    "/categories",
    "/aide-enseignants",
  ],

  admin: [
    "/tableau-de-bord",
    "/dossier-d-inscription",
    "/fiche-adherent",
    "/absences",
    "/calendrier",
    "/messages",
    "/chat",
    "/boutique",
    "/commandes-&-reglements",
    "/actualites",
    "/galerie-photos",
    "/lexique",
    "/arbitrage-judo",
    "/applications-judo",
    "/prepa-physique",
    "/documents-passage-ceinture",
    "/techniques-judo",
    "/techniques-quizz",
    "/vote-sondage-utilisateur",
    "/listing-adherents",
    "/appel-presence",
    "/tableau-appel",
    "/passage-ceinture",
    "/grade-cn",
    "/prepa-physique-coaching",
    "/suivi-competitions",
    "/evenements",
    "/palmares",
    "/todolist",
    "/techniques-enseignants",
    "/categories",
    "/aide-enseignants",
    "/dashboard-adherents",
    "/dashboard-comptabilite",
    "/details-adherent",
    "/listing-utilisateurs",
    "/listing-adherents-admin",
    "/reglements-inscriptions",
    "/dossiers-d-inscription",
    "/paiements",
    "/articles-admin",
    "/depenses-recettes",
    "/coachings",
    "/heures-enseignement",
    "/inventaire",
    "/pret-materiel",
    "/chat-page-admin",
    "/message-admin",
    "/vote-sondage-admin",
    "/pages-personnalisees",
    "/parametres",
    "/requetes-admin",
    "/abonnement",
    "/update",
    "/aide-conseils",
  ],
};

function getPathLabel(path) {
  const normalized = normalizePath(path);

  const found = Object.keys(PATH_LABELS).find(
    (key) => normalizePath(key) === normalized
  );

  return found ? PATH_LABELS[found] : "Page interne";
}

function getNearbyPages(currentPaths, currentIndex, radius = 3) {
  if (!currentPaths.length) return [];

  const index = currentIndex >= 0 ? currentIndex : 0;
  const wantedSize = radius * 2 + 1;

  let start = Math.max(0, index - radius);
  let end = Math.min(currentPaths.length, index + radius + 1);

  if (end - start < wantedSize) {
    if (start === 0) {
      end = Math.min(currentPaths.length, wantedSize);
    } else if (end === currentPaths.length) {
      start = Math.max(0, currentPaths.length - wantedSize);
    }
  }

  return currentPaths.slice(start, end).map((path, offset) => {
    const realIndex = start + offset;
    const distance = realIndex - index;

    return {
      path,
      index: realIndex,
      label: getPathLabel(path),
      isCurrent: distance === 0,
      isPrevious: distance === -1,
      isNext: distance === 1,
      distance,
    };
  });
}

export default function DashboardNavigation({ role }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [store, setStore] = useState({});
  const [loaded, setLoaded] = useState(false);

  const roleKey = toRoleKey(role);

  useEffect(() => {
    let mounted = true;

    async function fetchSettings() {
      try {
        const data = await getSettings();

        if (!mounted) return;

        setStore(unwrapSettings(data));
      } catch (error) {
        console.error("getSettings failed:", error);
      } finally {
        if (mounted) {
          setLoaded(true);
        }
      }
    }

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const currentPaths = useMemo(() => {
    if (!loaded) return [];

    const base = (paths[roleKey] || []).map((raw) => ({
      raw: raw.replace(/\/$/, ""),
      norm: normalizePath(raw),
    }));

    return base
      .filter(({ norm }) => {
        const key = DISPLAY_BY_PATH[norm];

        if (!key) return true;

        return isTrue(store[key]);
      })
      .map((item) => item.raw);
  }, [loaded, roleKey, store]);

  const currentIndex = useMemo(() => {
    const currentNorm = normalizePath(location.pathname);

    return currentPaths.findIndex((path) => normalizePath(path) === currentNorm);
  }, [currentPaths, location.pathname]);

  const safeIndex = currentIndex === -1 ? 0 : currentIndex;

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < currentPaths.length - 1;

  const previousPath = hasPrevious ? currentPaths[currentIndex - 1] : null;
  const nextPath = hasNext ? currentPaths[currentIndex + 1] : null;

  const previousLabel = previousPath ? getPathLabel(previousPath) : "Début";
  const nextLabel = nextPath ? getPathLabel(nextPath) : "Fin";

  const total = currentPaths.length || 1;
  const progress = total > 1 ? ((safeIndex + 1) / total) * 100 : 100;

  const nearbyPages = useMemo(
    () => getNearbyPages(currentPaths, currentIndex, 3),
    [currentPaths, currentIndex]
  );

  const goTo = (path) => {
    if (!path) return;
    navigate(path);
  };

  if (!loaded) {
    return (
      <div className="rounded-[1.35rem] border border-slate-200/80 bg-white/95 p-2 shadow-sm dark:border-slate-800 dark:bg-slate-900/95">
        <div className="h-[72px] animate-pulse rounded-[1.1rem] bg-slate-100 dark:bg-slate-800" />
      </div>
    );
  }

  return (
    <nav
      className="overflow-hidden rounded-[1.35rem] border border-slate-200/80 bg-white/95 p-2 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-900/95"
      aria-label="Navigation interne du tableau de bord"
    >
      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
        <StepButton
          direction="previous"
          disabled={!hasPrevious}
          label={previousLabel}
          onClick={() => goTo(previousPath)}
        />

        <div className="flex min-w-[72px] flex-col items-center justify-center rounded-2xl bg-slate-50 px-2 py-2 text-center ring-1 ring-slate-200/70 dark:bg-slate-950/70 dark:ring-slate-800">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Page
          </span>

          <span className="mt-0.5 text-sm font-bold text-slate-950 dark:text-white">
            {safeIndex + 1}/{total}
          </span>
        </div>

        <StepButton
          direction="next"
          disabled={!hasNext}
          label={nextLabel}
          onClick={() => goTo(nextPath)}
        />
      </div>

      {nearbyPages.length > 1 && (
        <div className="mt-2 rounded-2xl bg-slate-50 px-2 py-2 ring-1 ring-slate-200/70 dark:bg-slate-950/60 dark:ring-slate-800">
          <div className="flex items-center gap-1.5 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {nearbyPages.map((item) => (
              <TimelineItem
                key={item.path}
                item={item}
                onClick={() => {
                  if (item.isPrevious || item.isNext) {
                    goTo(item.path);
                  }
                }}
              />
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
        <div
          className="h-full rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-orange-400 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </nav>
  );
}

function StepButton({ direction, disabled, label, onClick }) {
  const isPrevious = direction === "previous";
  const Icon = isPrevious ? FaArrowLeft : FaArrowRight;
  const title = isPrevious ? "Précédent" : "Suivant";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={disabled ? title : `${title} : ${label}`}
      className={`group flex min-h-[58px] items-center gap-2 rounded-2xl px-2.5 py-2 transition active:scale-[0.98] sm:px-3 ${
        disabled
          ? "cursor-not-allowed bg-slate-50 text-slate-300 ring-1 ring-slate-200/70 dark:bg-slate-950/60 dark:text-slate-700 dark:ring-slate-800"
          : isPrevious
          ? "bg-orange-50 text-orange-950 ring-1 ring-orange-200 hover:bg-orange-100 dark:bg-orange-950/25 dark:text-orange-100 dark:ring-orange-900/70 dark:hover:bg-orange-950/40"
          : "bg-sky-50 text-sky-950 ring-1 ring-sky-200 hover:bg-sky-100 dark:bg-sky-950/25 dark:text-sky-100 dark:ring-sky-900/70 dark:hover:bg-sky-950/40"
      } ${isPrevious ? "justify-start text-left" : "justify-end text-right"}`}
      aria-label={disabled ? title : `${title} : ${label}`}
    >
      {isPrevious && (
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm transition ${
            disabled
              ? "bg-slate-100 text-slate-300 dark:bg-slate-900 dark:text-slate-700"
              : "bg-white/80 text-orange-700 shadow-sm dark:bg-orange-900/40 dark:text-orange-100"
          }`}
        >
          <Icon />
        </span>
      )}

      <span className="min-w-0">
        <span
          className={`block text-[9px] font-bold uppercase tracking-[0.14em] ${
            disabled
              ? "text-slate-300 dark:text-slate-700"
              : isPrevious
              ? "text-orange-500 dark:text-orange-300"
              : "text-sky-500 dark:text-sky-300"
          }`}
        >
          {title}
        </span>

        <span className="mt-0.5 block max-w-[110px] truncate text-xs font-bold sm:max-w-[180px] sm:text-sm">
          {label}
        </span>
      </span>

      {!isPrevious && (
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm transition ${
            disabled
              ? "bg-slate-100 text-slate-300 dark:bg-slate-900 dark:text-slate-700"
              : "bg-white/80 text-sky-700 shadow-sm dark:bg-sky-900/40 dark:text-sky-100"
          }`}
        >
          <Icon />
        </span>
      )}
    </button>
  );
}

function TimelineItem({ item, onClick }) {
  const isClickable = item.isPrevious || item.isNext;

  const baseClass =
    "shrink-0 rounded-full px-3 py-1.5 text-[11px] font-bold transition";

  let stateClass =
    "bg-white text-slate-400 ring-1 ring-slate-200/80 dark:bg-slate-900 dark:text-slate-500 dark:ring-slate-800";

  if (item.isCurrent) {
    stateClass =
      "bg-slate-950 text-white shadow-sm ring-1 ring-slate-950 dark:bg-white dark:text-slate-950 dark:ring-white";
  }

  if (item.isPrevious) {
    stateClass =
      "cursor-pointer bg-orange-100 text-orange-800 ring-1 ring-orange-200 hover:bg-orange-200 dark:bg-orange-950/40 dark:text-orange-100 dark:ring-orange-900/70 dark:hover:bg-orange-950/60";
  }

  if (item.isNext) {
    stateClass =
      "cursor-pointer bg-sky-100 text-sky-800 ring-1 ring-sky-200 hover:bg-sky-200 dark:bg-sky-950/40 dark:text-sky-100 dark:ring-sky-900/70 dark:hover:bg-sky-950/60";
  }

  return (
    <button
      type="button"
      disabled={!isClickable}
      onClick={onClick}
      aria-current={item.isCurrent ? "page" : undefined}
      className={`${baseClass} ${stateClass} ${
        isClickable ? "active:scale-[0.98]" : "cursor-default"
      }`}
      title={item.label}
    >
      <span className="hidden sm:inline">{item.index + 1}. </span>
      <span>{item.label}</span>
    </button>
  );
}